import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import { slugify, isValidSlug } from "@/lib/crm-fields/slugify";
import { defaultTagColor } from "@/lib/crm-fields/tag-colors";
import { isDescendant, type Node as TagNode } from "@/lib/crm-fields/tag-tree";
import type { CustomFieldDef, CustomFieldValue, EntityType, LeadSource, Tag, Tagging } from "./types";

export const crmFieldsKeys = {
  defs:    (org?: string, entity?: EntityType) => ["crm-fields","defs",org,entity] as const,
  values:  (entityType: EntityType, entityId: string) => ["crm-fields","values",entityType,entityId] as const,
  tags:    (org?: string) => ["crm-fields","tags",org] as const,
  tagsOn:  (entityType: EntityType, entityId: string) => ["crm-fields","taggings",entityType,entityId] as const,
  leadSources: (org?: string) => ["crm-fields","lead-sources",org] as const,
};

// relay schema helper for legacy config tables that still require narrow casts.
type MutationResult = { error: Error | null };
type AnyTbl = {
  insert: (v: Record<string, unknown>) => PromiseLike<MutationResult>;
  update: (v: Record<string, unknown>) => { eq: (c: string, v: string) => PromiseLike<MutationResult> };
  upsert: (v: Record<string, unknown> | Record<string, unknown>[], opts?: { onConflict: string }) => PromiseLike<MutationResult>;
  delete: () => { eq: (c: string, v: string) => PromiseLike<MutationResult> };
};
const tbl = (name: string): AnyTbl => supabase.schema("relay").from(name as never) as unknown as AnyTbl;

const throwIfMutationError = ({ error }: MutationResult) => {
  if (error) throw error;
};

// ── custom_field_defs ──────────────────────────────────────────────────
export function useCustomFieldDefs(entity: EntityType) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: crmFieldsKeys.defs(organizationId ?? undefined, entity),
    enabled: !!organizationId,
    queryFn: async (): Promise<CustomFieldDef[]> => {
      if (!organizationId) return [];
      // organization_id filter is required, not just defense-in-depth: RLS
      // returns defs from every org the caller belongs to, so a member of
      // multiple orgs would otherwise see (and be able to pick) another
      // organization's fields here — e.g. DateRules.tsx's custom-field
      // picker, which then fails the same-organization trigger on save
      // (Codex P2 finding, PR #5634 round 1).
      const { data, error } = await supabase.schema("relay").from("custom_field_defs" as never)
        .select("*").eq("organization_id", organizationId).eq("entity_type", entity).order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as CustomFieldDef[];
    },
  });
}

export function useUpsertCustomFieldDef() {
  const qc = useQueryClient(); const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (def: Partial<CustomFieldDef> & { entity_type: EntityType; field_key: string; label: string; field_type: CustomFieldDef["field_type"] }) => {
      if (!organizationId) throw new Error("No organization");
      const row = { organization_id: organizationId, options: def.options ?? [], is_required: !!def.is_required,
                    sort_order: def.sort_order ?? 0, archived: !!def.archived, ...def };
      throwIfMutationError(await tbl("custom_field_defs").upsert(
        row as Record<string, unknown>,
        { onConflict: "organization_id,entity_type,field_key" },
      ));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields","defs"] }); },
  });
}

export function useDeleteCustomFieldDef() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { throwIfMutationError(await tbl("custom_field_defs").delete().eq("id", id)); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields","defs"] }); },
  });
}

// ── custom_field_values (per record) ──────────────────────────────────
export function useCustomFieldValues(entity: EntityType, entityId: string | undefined) {
  return useQuery({
    queryKey: crmFieldsKeys.values(entity, entityId ?? ""),
    enabled: !!entityId,
    queryFn: async (): Promise<CustomFieldValue[]> => {
      if (!entityId) return [];
      const { data, error } = await supabase.schema("relay").from("custom_field_values" as never)
        .select("*").eq("entity_type", entity).eq("entity_id", entityId);
      if (error) throw error;
      return (data ?? []) as unknown as CustomFieldValue[];
    },
  });
}

export function useSetCustomFieldValue() {
  const qc = useQueryClient(); const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (args: { field_id: string; entity_type: EntityType; entity_id: string; value: unknown }) => {
      if (!organizationId) throw new Error("No organization");
      throwIfMutationError(await tbl("custom_field_values").upsert(
        { organization_id: organizationId, ...args, updated_at: new Date().toISOString() } as Record<string, unknown>,
        { onConflict: "field_id,entity_id" },
      ));
    },
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: crmFieldsKeys.values(args.entity_type, args.entity_id) });
    },
  });
}

// ── tags ──────────────────────────────────────────────────────────────
export function useTags(orgId?: string) {
  const { organizationId } = useOrganization();
  const targetOrgId = orgId ?? organizationId;
  return useQuery({
    queryKey: crmFieldsKeys.tags(targetOrgId ?? undefined),
    enabled: !!targetOrgId,
    queryFn: async (): Promise<Tag[]> => {
      if (!targetOrgId) return [];
      const { data, error } = await supabase.schema("relay").from("tags" as never)
        .select("*").eq("organization_id", targetOrgId).order("name");
      if (error) throw error;
      return (data ?? []) as unknown as Tag[];
    },
  });
}

export function useUpsertTag() {
  const qc = useQueryClient(); const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (tag: Partial<Tag> & { name: string; slug: string }) => {
      if (!organizationId) throw new Error("No organization");
      const row = {
        organization_id: organizationId,
        archived: false,
        parent_id: null,
        color: null,
        ...tag,
      };

      if (row.id) {
        // relay.tags has no cycle guard (Forge 20260521023442), and a cycle
        // hangs every parent walk and drops its tags off the Settings tree
        // (relay#245). Checked against the saved tree, not a cached one.
        if (row.parent_id) {
          const { data: nodes, error: treeError } = await supabase
            .schema("relay")
            .from("tags" as never)
            .select("id, parent_id")
            .eq("organization_id", organizationId);
          if (treeError) throw treeError;
          if (isDescendant(row.parent_id, row.id, (nodes ?? []) as unknown as TagNode[])) {
            throw new Error("A tag can't be moved under itself or one of its sub-tags.");
          }
        }
        throwIfMutationError(await tbl("tags").upsert(row as Record<string, unknown>, { onConflict: "id" }));
        return;
      }

      // Reuse only the tag this one would collide with: the same slug under
      // the same parent, which is what the partial unique indexes scope a
      // slug to. Any other match is a different tag (relay#245).
      const sameSlug = supabase
        .schema("relay")
        .from("tags" as never)
        .select("id")
        .eq("organization_id", organizationId)
        .eq("slug", row.slug);
      const { data: existingTag, error: findError } = await (row.parent_id
        ? sameSlug.eq("parent_id", row.parent_id)
        : sameSlug.is("parent_id", null)
      ).maybeSingle();
      if (findError) throw findError;

      if ((existingTag as unknown as { id: string } | null)?.id) {
        throwIfMutationError(await tbl("tags").upsert({ ...row, id: (existingTag as unknown as { id: string }).id } as Record<string, unknown>, { onConflict: "id" }));
        return;
      }

      throwIfMutationError(await tbl("tags").insert(row as Record<string, unknown>));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields","tags"] }); },
  });
}

/**
 * Recolor a single tag with a targeted column update rather than a full-row
 * upsert — avoids re-sending immutable/server-managed columns (created_at,
 * updated_at) and the unrelated row defaults that a `{ ...t }` upsert would.
 */
export function useUpdateTagColor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, color }: { id: string; color: string | null }) => {
      throwIfMutationError(await tbl("tags").update({ color }).eq("id", id));
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields","tags"] }); },
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { throwIfMutationError(await tbl("tags").delete().eq("id", id)); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-fields","tags"] });
      qc.invalidateQueries({ queryKey: ["crm-fields","taggings"] });
    },
    // A tag still referenced by an inactivity rule's tag_id now fails at
    // the DB with ON DELETE RESTRICT (Codex, PR #5515) instead of
    // silently converting that rule into an unfiltered, org-wide one via
    // ON DELETE SET NULL. The global MutationCache.onError (lib/query-client.ts) only
    // logs telemetry — without a toast here the delete button would fail
    // with no visible feedback at all.
    onError: (error) => {
      toast.error("Couldn't delete tag", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });
}

/**
 * Create a root-level tag from a display name and return the row. Idempotent:
 * if a root tag with the same slug already exists it's returned instead of
 * inserting a duplicate. Used by inline "create & add" pickers so users don't
 * have to pre-create tags in the admin screen.
 *
 * `color` is optional; when omitted, an on-brand color is assigned by cycling
 * the shared palette (deterministic by slug) so inline-created tags are never
 * colorless. An existing tag is returned with its color untouched — recoloring
 * is the admin screen's job, not a side effect of "create & add".
 */
export function useCreateTag() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async ({ name, color }: { name: string; color?: string | null }): Promise<Tag> => {
      if (!organizationId) throw new Error("No organization");
      const trimmed = name.trim();
      const slug = slugify(trimmed);
      if (!trimmed || !isValidSlug(slug)) throw new Error("Invalid tag name");

      const cols =
        "id, organization_id, name, slug, color, parent_id, archived, created_at, updated_at";

      // Reuse an existing root tag with the same slug if present.
      const { data: existing, error: findError } = await supabase
        .schema("relay")
        .from("tags" as never)
        .select(cols)
        .eq("organization_id", organizationId)
        .is("parent_id", null)
        .eq("slug", slug)
        .maybeSingle();
      if (findError) throw findError;
      if (existing) return existing as unknown as Tag;

      const insertable = supabase.schema("relay").from("tags" as never) as unknown as {
        insert: (v: Record<string, unknown>) => {
          select: (cols: string) => { single: () => PromiseLike<{ data: unknown; error: Error | null }> };
        };
      };
      const { data, error } = await insertable
        .insert({
          organization_id: organizationId,
          name: trimmed,
          slug,
          parent_id: null,
          color: color ?? defaultTagColor(slug),
          archived: false,
        })
        .select(cols)
        .single();
      if (error) throw error;
      return data as unknown as Tag;
    },
    onSuccess: (tag) => {
      // Seed the list before the refetch lands, so a picker that selects the
      // new tag right away (TagSlugSelect) doesn't briefly show it as unknown.
      qc.setQueryData<Tag[]>(crmFieldsKeys.tags(organizationId ?? undefined), (old) =>
        old && !old.some((t) => t.id === tag.id) ? [...old, tag] : old,
      );
      qc.invalidateQueries({ queryKey: ["crm-fields", "tags"] });
    },
  });
}

// ── taggings (per record) ─────────────────────────────────────────────
export function useTaggings(entity: EntityType, entityId: string | undefined) {
  return useQuery({
    queryKey: crmFieldsKeys.tagsOn(entity, entityId ?? ""),
    enabled: !!entityId,
    queryFn: async (): Promise<Tagging[]> => {
      if (!entityId) return [];
      const { data, error } = await supabase.schema("relay").from("taggings" as never)
        .select("*").eq("entity_type", entity).eq("entity_id", entityId);
      if (error) throw error;
      return (data ?? []) as unknown as Tagging[];
    },
  });
}

export function useTaggingsForEntityType(entity: EntityType) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["crm-fields","taggings-all",organizationId,entity],
    enabled: !!organizationId,
    queryFn: async (): Promise<Tagging[]> => {
      const { data, error } = await supabase.schema("relay").from("taggings" as never)
        .select("*").eq("entity_type", entity);
      if (error) throw error;
      return (data ?? []) as unknown as Tagging[];
    },
  });
}

export function useApplyTag() {
  const qc = useQueryClient(); const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (args: { tag_id: string; entity_type: EntityType; entity_id: string }) => {
      if (!organizationId) throw new Error("No organization");
      throwIfMutationError(await tbl("taggings").upsert(
        { organization_id: organizationId, ...args } as Record<string, unknown>,
        { onConflict: "tag_id,entity_type,entity_id" },
      ));
    },
    onSuccess: (_d, args) => {
      qc.invalidateQueries({ queryKey: crmFieldsKeys.tagsOn(args.entity_type, args.entity_id) });
    },
  });
}

export function useRemoveTagging() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { throwIfMutationError(await tbl("taggings").delete().eq("id", id)); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields","taggings"] }); },
  });
}


/** Apply a single tag to multiple entities at once. */
export function useBulkApplyTag() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async ({
      tag_id,
      entity_type,
      entity_ids,
    }: {
      tag_id: string;
      entity_type: EntityType;
      entity_ids: string[];
    }): Promise<void> => {
      if (!organizationId || entity_ids.length === 0) return;
      const rows = entity_ids.map((entity_id) => ({
        organization_id: organizationId,
        tag_id,
        entity_type,
        entity_id,
      }));
      throwIfMutationError(
        await tbl('taggings').upsert(rows as Record<string, unknown>[], {
          onConflict: 'tag_id,entity_type,entity_id',
        }),
      );
    },
    onSuccess: (_d, { entity_type }) => {
      qc.invalidateQueries({ queryKey: ['crm-fields', 'taggings-all', organizationId, entity_type] });
      qc.invalidateQueries({ queryKey: ['crm-fields', 'taggings'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not apply tag'),
  });
}

// ── lead sources (org-defined custom sources) ─────────────────────────
export function useLeadSources() {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: crmFieldsKeys.leadSources(organizationId ?? undefined),
    enabled: !!organizationId,
    queryFn: async (): Promise<LeadSource[]> => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .schema("relay")
        .from("lead_sources")
        .select("*")
        .eq("organization_id", organizationId)
        .order("name");
      if (error) throw error;
      return (data ?? []) as unknown as LeadSource[];
    },
  });
}

/**
 * Create an org custom lead source and return the row. Idempotent on
 * case-insensitive name: an existing source with the same name (archived or
 * not) is unarchived and returned instead of inserting a duplicate.
 */
export function useCreateLeadSource() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (name: string): Promise<LeadSource> => {
      if (!organizationId) throw new Error("No organization");
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Source name is required");

      const cols = "id, organization_id, name, archived, created_at, updated_at";
      // Find an existing source by case-insensitive exact name. We compare in
      // JS rather than ILIKE so names containing LIKE metacharacters (e.g.
      // "100% Referral", "Conference_2026") match exactly instead of being
      // treated as patterns. Per-org source lists are small.
      const { data: rows, error: findError } = await supabase
        .schema("relay")
        .from("lead_sources")
        .select(cols)
        .eq("organization_id", organizationId);
      if (findError) throw findError;
      const wanted = trimmed.toLowerCase();
      const existing = ((rows ?? []) as unknown as LeadSource[]).find(
        (s) => s.name.trim().toLowerCase() === wanted,
      );
      if (existing) {
        if (existing.archived) {
          const { error: updateError } = await supabase
            .schema("relay")
            .from("lead_sources")
            .update({ archived: false })
            .eq("id", existing.id);
          if (updateError) throw updateError;
          return { ...existing, archived: false };
        }
        return existing;
      }

      const { data, error } = await supabase
        .schema("relay")
        .from("lead_sources")
        .insert({ organization_id: organizationId, name: trimmed, archived: false })
        .select(cols)
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm-fields", "lead-sources"] });
    },
  });
}

export function useUpdateLeadSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<LeadSource> & { id: string }) => {
      const { id, ...rest } = input;
      const { error } = await supabase.schema("relay").from("lead_sources").update(rest).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields", "lead-sources"] }); },
  });
}

export function useDeleteLeadSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.schema("relay").from("lead_sources").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-fields", "lead-sources"] }); },
  });
}
